// FR-RD14: the resume state machine. Pure and dependency-injected (api client, LocalSource,
// queue factory) so every rule below is unit-testable with fakes — see stateMachine.test.ts.
//
// Rules (brief scope item 2):
//   - recover sessions in order, one at a time (never concurrently)
//   - replay chunks, skip already-accepted parts (UploadQueue's resumeSeed handles this)
//   - complete, then delete local data ONLY after complete succeeded (or a 404 proved an
//     earlier run's complete already succeeded)
//   - unreadable chunk/segment: keep the data, mark the session failed, move to the next one
//   - network error/offline: leave as pending_upload, never delete, caller retries later
import type {
  LocalSource,
  PersistedUploadState,
  QueueFactory,
  RecoveryApi,
  RecoveryEvent,
} from "./types";
import { RecoveryNetworkError } from "./types";

function describeError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "recovery failed";
}

/** A session whose `liveHeartbeatAt` is this recent belongs to a live recorder (it re-stamps it
 * every few seconds while capturing, and on every accepted part) — never recover it out from
 * under that recorder. Keyed on the live-only field, not `updatedAt`/status: a recovery
 * attempt's own late writes (a part PUT landing after the attempt was halted) must not make the
 * session look live and hide it from the next sweep. This is the guard
 * that holds across tabs and reloads regardless of the in-memory/Web Lock checks in
 * activeSessions.ts: a sweep that resumed a live session completed its grackle from the first
 * few seconds of chunks, after which every part the live recorder cut was refused (404) and the
 * recording sat at "0 of N parts uploaded". A crashed tab stops writing, so its session becomes
 * recoverable once this window passes. */
export const LIVE_SESSION_FRESH_MS = 2 * 60_000;

export interface RunRecoveryDeps {
  localSource: LocalSource;
  queueFactory: QueueFactory;
  api: RecoveryApi;
  onEvent?: (event: RecoveryEvent) => void;
  /** The signed-in user's id. Sessions persisted with a different `userId` are skipped
   * silently — they belong to whoever recorded them and wait for that person to sign in. */
  ownerId?: string;
}

export interface RecoverSessionOptions {
  /** Manual "Upload now": retry a session previously marked failed instead of only
   * re-surfacing it. */
  retryFailed?: boolean;
}

/** Recovers every local session, strictly in order, awaiting each one fully before starting
 * the next (brief: "one at a time"). A session that fails or goes to pending_upload does not
 * block the rest. */
export async function runRecovery(deps: RunRecoveryDeps): Promise<void> {
  const ids = await deps.localSource.list();
  for (const id of ids) {
    await recoverSession(id, deps);
  }
}

/** Recovers exactly one session by id — same rules as `runRecovery`, for a manual "Upload
 * now" trigger (RecorderPage) rather than the automatic on-launch sweep. */
export async function recoverSession(
  id: string,
  deps: RunRecoveryDeps,
  opts: RecoverSessionOptions = {},
): Promise<void> {
  const state = await deps.localSource.loadState(id);
  if (!state) return;
  if (deps.ownerId && state.userId && state.userId !== deps.ownerId) return; // not ours
  if (
    !opts.retryFailed &&
    state.liveHeartbeatAt !== undefined &&
    Date.now() - state.liveHeartbeatAt < LIVE_SESSION_FRESH_MS
  ) {
    return; // a live recorder is still writing this session
  }
  if (state.status === "failed" && !opts.retryFailed) {
    // Failed sessions wait on the user: re-surface them (no upload attempt) so the UI keeps
    // offering "Export file" / "Discard" on every launch until one is chosen.
    deps.onEvent?.({ type: "failed", sessionId: id, title: state.title });
    return;
  }

  let chunks: Blob[];
  try {
    chunks = await deps.localSource.readChunks(id);
  } catch (err) {
    await deps.localSource.markFailed(id, describeError(err));
    deps.onEvent?.({ type: "failed", sessionId: id, title: state.title });
    return;
  }

  await attempt(id, state, chunks, deps, /* allowRecreate */ true);
}

/** One upload attempt for a session. May recurse exactly once, with a cleared grackleId, if
 * the server-side upload turns out to be expired (never unbounded — `allowRecreate` is only
 * ever true on the first attempt). */
async function attempt(
  id: string,
  initialState: PersistedUploadState,
  chunks: Blob[],
  deps: RunRecoveryDeps,
  allowRecreate: boolean,
): Promise<void> {
  let working = initialState;
  const resumeSeed = working.grackleId
    ? { grackleId: working.grackleId, partSize: working.partSize, completedParts: working.completedParts }
    : null;

  const queue = deps.queueFactory({
    sessionId: id,
    orgId: working.orgId,
    title: working.title,
    contentType: working.contentType,
    resumeSeed,
    onSessionCreated: (grackleId, partSize) => {
      working = { ...working, grackleId, uploadId: grackleId, partSize, status: "uploading", updatedAt: Date.now() };
      void deps.localSource.saveState(id, working);
    },
    onPartAccepted: (part) => {
      const completedParts = [...working.completedParts.filter((p) => p.n !== part.n), part];
      working = { ...working, completedParts, status: "uploading", updatedAt: Date.now() };
      void deps.localSource.saveState(id, working);
    },
  });

  deps.onEvent?.({ type: "recovered", sessionId: id, title: working.title });
  for (const chunk of chunks) queue.push(chunk);

  try {
    const result = await queue.finish();
    working = {
      ...working,
      finalized: true,
      grackleId: result.grackleId,
      uploadId: result.grackleId,
      status: "uploading",
      updatedAt: Date.now(),
    };
    await deps.localSource.saveState(id, working);
    await deps.localSource.deleteLocal(id);
    deps.onEvent?.({ type: "completed", sessionId: id, title: working.title });
  } catch (err) {
    if (err instanceof RecoveryNetworkError) {
      working = { ...working, status: "pending_upload", lastError: err.message, updatedAt: Date.now() };
      await deps.localSource.saveState(id, working);
      deps.onEvent?.({ type: "pending", sessionId: id, title: working.title });
      return;
    }

    // Terminal (non-network) error. If this attempt resumed a real server-side upload, the
    // brief's disambiguation applies: every /uploads/{id}/* route 404s identically whether an
    // earlier run's complete() already succeeded or the multipart session genuinely expired.
    if (working.grackleId && allowRecreate) {
      const statusInfo = await deps.api.getGrackleStatus(working.grackleId);
      const localBytes = chunks.reduce((sum, c) => sum + c.size, 0);
      if (
        statusInfo &&
        statusInfo.status !== "uploading" &&
        statusInfo.sourceBytes !== null &&
        statusInfo.sourceBytes >= localBytes
      ) {
        // The earlier run's complete() actually landed with everything we hold (crash happened
        // right after) — the publish job is already enqueued. Delete local data; never
        // re-upload (no duplicate grackles).
        await deps.localSource.deleteLocal(id);
        deps.onEvent?.({ type: "completed", sessionId: id, title: working.title });
        return;
      }
      // FR-RD7: a grackle completed from FEWER bytes than this device holds (a racing queue
      // finished it from a prefix of the chunks) is not "done" — deleting here would destroy
      // the only full copy. Falls through to a fresh upload of everything; a duplicate grackle
      // is recoverable, a deleted recording is not.
      //
      // Gone, not ours, or still stuck "uploading" with an unusable multipart session: there
      // is no server route to re-open an upload on the same grackle, so the only fix is a new
      // one. Local data is untouched until ITS complete succeeds.
      const fresh: PersistedUploadState = {
        ...working,
        grackleId: null,
        uploadId: null,
        completedParts: [],
        finalized: false,
        status: "pending_upload",
        updatedAt: Date.now(),
      };
      await deps.localSource.saveState(id, fresh);
      await attempt(id, fresh, chunks, deps, false);
      return;
    }

    await deps.localSource.markFailed(id, describeError(err));
    deps.onEvent?.({ type: "failed", sessionId: id, title: working.title });
  }
}
